/**
 * /admin/v/:orderId?t=<token>
 *
 * One-click admin verify-transfer page (iter88).
 *
 * Lands here from the SMS / email sent at "buyer reported transfer" time.
 * Shows the order summary + a green "Confirm received & activate" button.
 * On confirm:
 *   - lead_pack.status flips to "paid"
 *   - invoice.status flips to "paid"
 *   - buyer gets confirmation email + SMS ("Your campaign is live")
 *
 * Mobile-first layout — built to be tapped from the phone notification.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle, ArrowLeft, Banknote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminVerifyTransfer() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const { user, loading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("checking"); // checking | ready | confirmed | error
  const [errMsg, setErrMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Wait for auth resolution; redirect to login if not admin
    if (authLoading) return;
    if (!user) {
      // Bounce through login, preserving the verify URL so they return here after sign-in
      const back = `/admin/v/${orderId}?t=${encodeURIComponent(token || "")}`;
      window.location.href = `/login?next=${encodeURIComponent(back)}`;
      return;
    }
    if (user.role !== "admin") {
      setState("error");
      setErrMsg("Admin access required to verify transfers.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/admin/transfer-verify/${orderId}`, { params: { t: token } });
        setOrder(data.order);
        if (data.order?.status === "paid" || data.order?.transfer_verified_at) {
          setState("confirmed");
        } else {
          setState("ready");
        }
      } catch (e) {
        setState("error");
        setErrMsg(e?.response?.data?.detail || "Could not load this verify request.");
      }
    })();
  }, [orderId, token, user, authLoading]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/transfer-verify/${orderId}`, null, { params: { t: token } });
      if (data?.already_verified) {
        toast.info("Already verified.");
      } else {
        toast.success("Funds confirmed — buyer notified, campaign live.");
      }
      // refresh order
      const { data: re } = await api.get(`/admin/transfer-verify/${orderId}`, { params: { t: token } });
      setOrder(re.order);
      setState("confirmed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/40 flex flex-col">
      {/* Compact top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-2xl px-4 py-3 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-[13px] text-slate-600 hover:text-slate-900">
            <ArrowLeft className="size-3.5" /> Admin
          </Link>
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-mono text-slate-500">Verify transfer</div>
        </div>
      </header>

      <main className="flex-1 px-4 py-8 sm:py-14">
        <div className="mx-auto max-w-md">
          {state === "checking" && (
            <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center shadow-soft" data-testid="verify-checking">
              <Loader2 className="mx-auto size-7 text-slate-400 animate-spin" />
              <p className="mt-4 text-[13.5px] text-slate-600">Looking up this order…</p>
            </div>
          )}

          {state === "error" && (
            <div className="rounded-2xl bg-white border border-rose-200 p-8 text-center shadow-soft" data-testid="verify-error">
              <AlertTriangle className="mx-auto size-7 text-rose-500" />
              <h2 className="mt-4 text-lg font-semibold text-slate-900">Can't verify</h2>
              <p className="mt-2 text-[13px] text-slate-600">{errMsg}</p>
            </div>
          )}

          {(state === "ready" || state === "confirmed") && order && (
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-soft" data-testid="verify-card">
              {/* Status banner */}
              {state === "confirmed" ? (
                <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-4 flex items-center gap-3">
                  <CheckCircle2 className="size-5 text-emerald-600" strokeWidth={2} />
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.18em] font-mono text-emerald-700 font-semibold">Verified</div>
                    <div className="text-[12.5px] text-emerald-900">Buyer notified · campaign live</div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border-b border-blue-200 px-5 py-4 flex items-center gap-3">
                  <Banknote className="size-5 text-blue-600" strokeWidth={1.8} />
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.18em] font-mono text-blue-700 font-semibold">Action required</div>
                    <div className="text-[12.5px] text-blue-900">Confirm funds received in the bank account</div>
                  </div>
                </div>
              )}

              <div className="px-5 py-6">
                <div className="space-y-3 text-[13px]">
                  <Row label="Buyer" value={`${order.company || "—"}`} />
                  <Row label="Contact" value={`${order.contact_name || "—"} · ${order.contact_email || "—"}`} />
                  <Row label="Order" value={`${(order.quantity_ordered || 0).toLocaleString()} × ${order.sector_name || ""}`} />
                  <Row label="Invoice" value={order.invoice_number || order.id?.slice(0, 8)} mono />
                  <Row label="Amount" value={<span className="font-semibold tabular-nums text-slate-900">£{Number(order.total_gbp || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>} />
                  <Row label="Bank ref" value={order.bank_reference ? <span className="font-mono text-[#0055FF]">{order.bank_reference}</span> : "—"} />
                  {order.transfer_sent_at && (
                    <Row label="Reported" value={new Date(order.transfer_sent_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} />
                  )}
                  {state === "confirmed" && order.transfer_verified_at && (
                    <Row label="Verified by" value={<span className="text-emerald-700">{order.verified_by || "admin"}</span>} />
                  )}
                </div>

                {state === "ready" && (
                  <>
                    <Button
                      onClick={confirm}
                      disabled={busy}
                      className="mt-7 w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 font-semibold"
                      data-testid="verify-confirm-btn"
                    >
                      {busy ? <Loader2 className="size-4 animate-spin" /> : <><CheckCircle2 className="size-4" /> Confirm received &amp; activate</>}
                    </Button>
                    <p className="mt-3 text-[11px] text-slate-500 text-center leading-relaxed">
                      Tapping confirms the bank transfer has landed and instantly activates the buyer's campaign. The buyer will receive an email + SMS confirmation.
                    </p>
                  </>
                )}

                {state === "confirmed" && (
                  <Link
                    to="/admin"
                    className="mt-7 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium h-11 px-5 text-[14px]"
                    data-testid="verify-done-link"
                  >
                    Back to admin
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-baseline">
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`col-span-2 text-slate-800 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
